// @ts-nocheck
/*
 * Port of views/dashboard/notification/notification_assignments.js.
 * Dual view (agents/users) via segmented toggle, 6-field filter panel,
 * date range defaults (month start → today) clamped to last 3 months.
 * `isAllowed` derived from SUPERADMIN/SUB_SUPERADMIN OR any notification ACL bit.
 */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import {
  API_NOTIFICATION_ASSIGNED_TO_AGENT,
  API_NOTIFICATION_ASSIGNED_TO_USER,
} from '@/service/url';
import { USER_ROLES, USER_ROLES_MAP } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button } from '@/components/ui/button';
import {
  RefreshCw,
  Users,
  UsersRound,
  ShieldAlert,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const notifText = (rd) =>
  rd?.notification?.notification_type || rd?.notification_type || '';
const agentText = (rd) =>
  rd?.agent?.agent_name || rd?.agent?.username || (rd?.agent_id ?? '') + '';
const userText = (rd) =>
  rd?.assignedUser?.name || rd?.assignedUser?.username || (rd?.user_id ?? '') + '';
const roleText = (rd) =>
  USER_ROLES_MAP[rd?.assignedUser?.role] || rd?.assignedUser?.role || '';

const fmt = (v) => (v ? new Date(v).toLocaleString('en-IN') : '—');

const pad = (n) => String(n).padStart(2, '0');
const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const _today = new Date();
const TODAY_STR = toYMD(_today);
const FIRST_OF_MONTH_STR = toYMD(new Date(_today.getFullYear(), _today.getMonth(), 1));
const MIN_FROM_STR = toYMD(
  new Date(_today.getFullYear(), _today.getMonth() - 3, _today.getDate()),
);

const inDateRange = (createdAt, from, to) => {
  if (!from && !to) return true;
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  if (from && d < new Date(`${from}T00:00:00`)) return false;
  if (to && d > new Date(`${to}T23:59:59.999`)) return false;
  return true;
};

const ReadPill = ({ isRead }) =>
  isRead ? (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
      Read
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
      Unread
    </span>
  );

const NotificationAssignments = () => {
  const [loading, setLoading] = useState(false);
  const [isAllowed, setIsAllowed] = useState(true);
  const [agentAssignments, setAgentAssignments] = useState([]);
  const [userAssignments, setUserAssignments] = useState([]);
  const [view, setView] = usePersistedState('notification/NotificationAssignments:view', 'agents');

  const [notifFilter, setNotifFilter] = usePersistedState('notification/NotificationAssignments:notifFilter', '');
  const [agentFilter, setAgentFilter] = usePersistedState('notification/NotificationAssignments:agentFilter', '');
  const [userFilter, setUserFilter] = usePersistedState('notification/NotificationAssignments:userFilter', '');
  const [roleFilter, setRoleFilter] = usePersistedState('notification/NotificationAssignments:roleFilter', '');
  const [readFilter, setReadFilter] = usePersistedState('notification/NotificationAssignments:readFilter', '');
  const [dateFrom, setDateFrom] = usePersistedState('notification/NotificationAssignments:dateFrom', FIRST_OF_MONTH_STR);
  const [dateTo, setDateTo] = usePersistedState('notification/NotificationAssignments:dateTo', TODAY_STR);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const user = getUser();
        let acl = {};
        if (user?.acl_settings?.module_operations) {
          acl =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
        }
        const notifAcl = acl?.notification || acl?.Notification || {};
        const allowed =
          user?.role === USER_ROLES.SUPERADMIN ||
          user?.role === USER_ROLES.SUB_SUPERADMIN ||
          notifAcl.read === true ||
          notifAcl.view_permission === true ||
          notifAcl.add === true ||
          notifAcl.edit === true ||
          notifAcl.delete === true;
        setIsAllowed(Boolean(allowed));
      } catch (e) {
        toast.error(friendlyApiError(e) || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const fetchSeqRef = useRef(0);
  const fetchAssignments = async () => {
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const range = { from_date: dateFrom || undefined, to_date: dateTo || undefined };
      const [agentResp, userResp] = await Promise.allSettled([
        getApiCall(API_NOTIFICATION_ASSIGNED_TO_AGENT, range),
        getApiCall(API_NOTIFICATION_ASSIGNED_TO_USER, range),
      ]);
      if (seq !== fetchSeqRef.current) return;

      if (agentResp.status === 'fulfilled') {
        const data = agentResp.value?.data || agentResp.value;
        setAgentAssignments(Array.isArray(data) ? data : []);
      } else {
        setAgentAssignments([]);
      }

      if (userResp.status === 'fulfilled') {
        const data = userResp.value?.data || userResp.value;
        setUserAssignments(Array.isArray(data) ? data : []);
      } else {
        setUserAssignments([]);
      }
    } catch (e) {
      if (typeof e === 'string' && e.trim())
        toast.error(friendlyApiError(e) || 'Failed to load assignments');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAllowed) return;
    fetchAssignments();
  }, [isAllowed, dateFrom, dateTo]);

  const roleOptions = useMemo(() => {
    const set = new Set(
      userAssignments
        .map((r) => r?.assignedUser?.role)
        .filter((v) => v !== undefined && v !== null),
    );
    return Array.from(set);
  }, [userAssignments]);

  const matchesReadDate = (rd) => {
    if (readFilter === 'true' && !rd?.is_read) return false;
    if (readFilter === 'false' && rd?.is_read) return false;
    if (!inDateRange(rd?.created_at, dateFrom, dateTo)) return false;
    return true;
  };

  const filteredAgents = useMemo(() => {
    const n = notifFilter.trim().toLowerCase();
    const a = agentFilter.trim().toLowerCase();
    return agentAssignments.filter((rd) => {
      if (n && !notifText(rd).toLowerCase().includes(n)) return false;
      if (a && !agentText(rd).toLowerCase().includes(a)) return false;
      return matchesReadDate(rd);
    });
  }, [agentAssignments, notifFilter, agentFilter, readFilter, dateFrom, dateTo]);

  const filteredUsers = useMemo(() => {
    const n = notifFilter.trim().toLowerCase();
    const u = userFilter.trim().toLowerCase();
    return userAssignments.filter((rd) => {
      if (n && !notifText(rd).toLowerCase().includes(n)) return false;
      if (u && !userText(rd).toLowerCase().includes(u)) return false;
      if (roleFilter && String(rd?.assignedUser?.role) !== String(roleFilter))
        return false;
      return matchesReadDate(rd);
    });
  }, [userAssignments, notifFilter, userFilter, roleFilter, readFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setNotifFilter('');
    setAgentFilter('');
    setUserFilter('');
    setRoleFilter('');
    setReadFilter('');
    setDateFrom(FIRST_OF_MONTH_STR);
    setDateTo(TODAY_STR);
  };

  if (loading && agentAssignments.length === 0 && userAssignments.length === 0)
    return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Assigned Notifications"
        description="Notifications currently assigned to agents and users."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={fetchAssignments}
            disabled={loading || !isAllowed}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
        }
      />

      {!isAllowed ? (
        <div
          className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-center dark:border-rose-900/60 dark:bg-rose-950/40"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <ShieldAlert className="mx-auto h-8 w-8 text-rose-600 dark:text-rose-300" />
          <h4 className="mt-2 text-sm font-bold text-rose-700 dark:text-rose-300">
            Access denied
          </h4>
          <p className="mt-1 text-xs text-rose-600/80 dark:text-rose-300/80">
            You do not have permission to view assignments.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-3 flex gap-2">
            {[
              {
                key: 'agents',
                label: 'Agents',
                icon: Users,
                count: filteredAgents.length,
              },
              {
                key: 'users',
                label: 'Users',
                icon: UsersRound,
                count: filteredUsers.length,
              },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = view === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setView(tab.key)}
                  className={
                    'relative flex h-10 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                    (active
                      ? 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/25'
                      : 'border-border/60 bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground')
                  }
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  <span
                    className={
                      'ml-1 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ' +
                      (active
                        ? 'bg-white/90 text-foreground'
                        : 'bg-muted text-muted-foreground')
                    }
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div
            className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
            style={{
              boxShadow:
                '4px 4px 10px rgba(163, 177, 198, 0.28), -4px -4px 10px rgba(255, 255, 255, 0.85)',
            }}
          >
            <DateRangeFilter
              value={{ from: dateFrom, to: dateTo }}
              onChange={({ from, to }) => {
                setDateFrom(from);
                setDateTo(to);
              }}
              onReset={clearFilters}
              min={MIN_FROM_STR}
              max={TODAY_STR}
            >
              <div className="w-[180px]">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Notification
                </label>
                <input
                  placeholder="Search notification…"
                  value={notifFilter}
                  onChange={(e) => setNotifFilter(e.target.value)}
                  className="input-embossed h-10 w-full rounded-md px-2.5 text-xs"
                />
              </div>

              {view === 'agents' ? (
                <div className="w-[180px]">
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Agent
                  </label>
                  <input
                    placeholder="Search agent…"
                    value={agentFilter}
                    onChange={(e) => setAgentFilter(e.target.value)}
                    className="input-embossed h-10 w-full rounded-md px-2.5 text-xs"
                  />
                </div>
              ) : (
                <>
                  <div className="w-[180px]">
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      User
                    </label>
                    <input
                      placeholder="Search user…"
                      value={userFilter}
                      onChange={(e) => setUserFilter(e.target.value)}
                      className="input-embossed h-10 w-full rounded-md px-2.5 text-xs"
                    />
                  </div>
                  <div className="w-[150px]">
                    <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      Role
                    </label>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                      className="input-embossed h-10 w-full rounded-md px-2.5 text-xs"
                    >
                      <option value="">All</option>
                      {roleOptions.map((r) => (
                        <option key={r} value={r}>
                          {USER_ROLES_MAP[r] || r}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="w-[130px]">
                <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Read status
                </label>
                <select
                  value={readFilter}
                  onChange={(e) => setReadFilter(e.target.value)}
                  className="input-embossed h-10 w-full rounded-md px-2.5 text-xs"
                >
                  <option value="">All</option>
                  <option value="true">Read</option>
                  <option value="false">Unread</option>
                </select>
              </div>
            </DateRangeFilter>
          </div>

          <div
            className="rounded-2xl border border-border/60 bg-card p-4"
            style={{
              boxShadow:
                '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
            }}
          >
            <div className="overflow-x-auto rounded-lg border border-border/60">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold">Notification</th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {view === 'agents' ? 'Agent' : 'User'}
                    </th>
                    {view === 'users' && (
                      <th className="px-3 py-2 text-left font-semibold">Role</th>
                    )}
                    <th className="px-3 py-2 text-left font-semibold">Assigned At</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Read At</th>
                  </tr>
                </thead>
                <tbody>
                  {view === 'agents' ? (
                    filteredAgents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-8 text-center text-sm text-muted-foreground"
                        >
                          No agent assignments
                        </td>
                      </tr>
                    ) : (
                      filteredAgents.map((rd, idx) => (
                        <tr
                          key={idx}
                          className={`border-t border-border/40 ${idx % 2 ? 'bg-muted/30' : ''}`}
                        >
                          <td className="px-3 py-2 font-medium text-foreground">
                            {notifText(rd) || '—'}
                          </td>
                          <td className="px-3 py-2 text-foreground">
                            {agentText(rd) || '—'}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {fmt(rd?.created_at)}
                          </td>
                          <td className="px-3 py-2">
                            <ReadPill isRead={rd?.is_read} />
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {fmt(rd?.read_at)}
                          </td>
                        </tr>
                      ))
                    )
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-8 text-center text-sm text-muted-foreground"
                      >
                        No user assignments
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((rd, idx) => (
                      <tr
                        key={idx}
                        className={`border-t border-border/40 ${idx % 2 ? 'bg-muted/30' : ''}`}
                      >
                        <td className="px-3 py-2 font-medium text-foreground">
                          {notifText(rd) || '—'}
                        </td>
                        <td className="px-3 py-2 text-foreground">
                          {userText(rd) || '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {roleText(rd) || '—'}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {fmt(rd?.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          <ReadPill isRead={rd?.is_read} />
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {fmt(rd?.read_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </FillPage>
  );
};

export default NotificationAssignments;
